import { httpClient } from '@/shared/api';
import {
  ComplianceFollowUpSource,
  ComplianceFollowUpStatus,
  type AddReviewBody,
  type ComplianceActivityDto,
  type ComplianceCreateScope,
  type ComplianceFollowUpDto,
  type ComplianceFollowUpListResponse,
  type CreateComplianceFollowUpBody,
  type GetComplianceFollowUpListParams,
  type TakeActionBody,
} from './compliance.dto';

const PREFIX = '/app/compliance-follow-up';

function deadlineDateToIsoUtc(dateYmd: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateYmd)) {
    return new Date(dateYmd).toISOString();
  }
  return new Date(`${dateYmd}T12:00:00.000Z`).toISOString();
}

export function buildCreateComplianceBody(
  scope: ComplianceCreateScope,
  description: string,
  deadlineDateYmd: string
): CreateComplianceFollowUpBody {
  const deadline = deadlineDateToIsoUtc(deadlineDateYmd);
  if (scope.kind === 'school') {
    return {
      source: ComplianceFollowUpSource.SchoolProfile,
      schoolId: scope.schoolId,
      reportingYear: scope.reportingYear,
      riskTriageQueueItemId: null,
      description,
      deadline,
      currentStatus: ComplianceFollowUpStatus.Open,
      activities: [],
    };
  }
  return {
    source: ComplianceFollowUpSource.RiskTriage,
    schoolId: scope.schoolId,
    reportingYear: scope.reportingYear,
    riskTriageQueueItemId: scope.riskTriageQueueItemId,
    description,
    deadline,
    currentStatus: ComplianceFollowUpStatus.Open,
    activities: [],
  };
}

function readGuidish(v: unknown): string | undefined {
  if (v == null || v === '') return undefined;
  const s = String(v).trim();
  return s || undefined;
}

export function parseComplianceFollowUpCreateResponse(raw: unknown): string | undefined {
  if (raw == null || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const direct = readGuidish(o.id ?? o.Id);
  if (direct) return direct;
  const inner = o.result ?? o.Result;
  if (inner && typeof inner === 'object') {
    const r = inner as Record<string, unknown>;
    return readGuidish(r.id ?? r.Id);
  }
  return undefined;
}

function normalizeActivityDto(a: Record<string, unknown>): ComplianceActivityDto {
  return {
    id: String(a.id ?? a.Id ?? ''),
    complianceFollowUpId: String(a.complianceFollowUpId ?? a.ComplianceFollowUpId ?? ''),
    type: Number(a.type ?? a.Type ?? 0),
    note: String(a.note ?? a.Note ?? ''),
    newStatus: a.newStatus != null ? Number(a.newStatus) : a.NewStatus != null ? Number(a.NewStatus) : null,
    userName: String(a.userName ?? a.UserName ?? ''),
    creationTime: String(a.creationTime ?? a.CreationTime ?? ''),
    creatorId: a.creatorId != null ? String(a.creatorId) : a.CreatorId != null ? String(a.CreatorId) : null,
  };
}

function normalizeFollowUpDto(o: Record<string, unknown>): ComplianceFollowUpDto {
  const rawActivities = (o.activities ?? o.Activities) as unknown[] | undefined;
  return {
    id: String(o.id ?? o.Id ?? ''),
    source: Number(o.source ?? o.Source ?? 0),
    schoolId: String(o.schoolId ?? o.SchoolId ?? ''),
    schoolName: o.schoolName != null ? String(o.schoolName) : o.SchoolName != null ? String(o.SchoolName) : null,
    riskTriageQueueItemId: o.riskTriageQueueItemId != null ? String(o.riskTriageQueueItemId) : o.RiskTriageQueueItemId != null ? String(o.RiskTriageQueueItemId) : null,
    reportingYear: Number(o.reportingYear ?? o.ReportingYear ?? 0),
    description: String(o.description ?? o.Description ?? ''),
    deadline: String(o.deadline ?? o.Deadline ?? ''),
    currentStatus: Number(o.currentStatus ?? o.CurrentStatus ?? 1),
    resolvedAt: o.resolvedAt != null ? String(o.resolvedAt) : o.ResolvedAt != null ? String(o.ResolvedAt) : null,
    creationTime: String(o.creationTime ?? o.CreationTime ?? ''),
    creatorId: o.creatorId != null ? String(o.creatorId) : o.CreatorId != null ? String(o.CreatorId) : null,
    creatorName: o.creatorName != null ? String(o.creatorName) : o.CreatorName != null ? String(o.CreatorName) : null,
    lastModificationTime: o.lastModificationTime != null ? String(o.lastModificationTime) : o.LastModificationTime != null ? String(o.LastModificationTime) : null,
    activities: Array.isArray(rawActivities)
      ? rawActivities.map((x) => normalizeActivityDto(x as Record<string, unknown>))
      : [],
  };
}

function normalizeListResponse(raw: unknown): ComplianceFollowUpListResponse {
  if (!raw || typeof raw !== 'object') return { items: [], totalCount: 0 };
  const o = raw as Record<string, unknown>;
  const rawItems = (o.items ?? o.Items ?? o.result ?? o.Result) as unknown[] | Record<string, unknown> | undefined;
  let itemsArr: unknown[] = [];
  if (Array.isArray(rawItems)) {
    itemsArr = rawItems;
  } else if (rawItems && typeof rawItems === 'object' && !Array.isArray(rawItems)) {
    const nested = rawItems as Record<string, unknown>;
    const inner = (nested.items ?? nested.Items) as unknown[] | undefined;
    if (Array.isArray(inner)) itemsArr = inner;
  }
  const items = itemsArr.map((x) => normalizeFollowUpDto(x as Record<string, unknown>));
  const tc = o.totalCount ?? o.TotalCount;
  const totalCount = typeof tc === 'number' && Number.isFinite(tc) ? tc : items.length;
  return { items, totalCount };
}

export const complianceApi = {
  create: async (body: CreateComplianceFollowUpBody): Promise<unknown> => {
    return httpClient.post<unknown>(PREFIX, body);
  },

  createFromScope: async (
    scope: ComplianceCreateScope,
    description: string,
    deadlineDateYmd: string
  ): Promise<string | undefined> => {
    const body = buildCreateComplianceBody(scope, description, deadlineDateYmd);
    const raw = await complianceApi.create(body);
    return parseComplianceFollowUpCreateResponse(raw);
  },

  getList: async (params: GetComplianceFollowUpListParams): Promise<ComplianceFollowUpListResponse> => {
    const q = new URLSearchParams();
    if (params.schoolId) q.set('SchoolId', params.schoolId);
    if (params.riskTriageQueueItemId) q.set('RiskTriageQueueItemId', params.riskTriageQueueItemId);
    if (params.year != null) q.set('Year', String(params.year));
    if (params.source != null) q.set('Source', String(params.source));
    if (params.status != null) q.set('Status', String(params.status));
    q.set('SkipCount', String(params.skipCount ?? 0));
    q.set('MaxResultCount', String(params.maxResultCount ?? 100));
    const qs = q.toString();
    const raw = await httpClient.get<unknown>(`${PREFIX}?${qs}`);
    return normalizeListResponse(raw);
  },

  getById: async (id: string): Promise<ComplianceFollowUpDto> => {
    return httpClient.get<ComplianceFollowUpDto>(`${PREFIX}/${id}`);
  },

  update: async (id: string, body: Partial<CreateComplianceFollowUpBody>): Promise<ComplianceFollowUpDto> => {
    return httpClient.put<ComplianceFollowUpDto>(`${PREFIX}/${id}`, body);
  },

  delete: async (id: string): Promise<void> => {
    await httpClient.delete<unknown>(`${PREFIX}/${id}`);
  },

  takeAction: async (id: string, body: TakeActionBody): Promise<unknown> => {
    return httpClient.post<unknown>(`${PREFIX}/${id}/take-action`, body);
  },

  addReview: async (id: string, body: AddReviewBody): Promise<unknown> => {
    return httpClient.post<unknown>(`${PREFIX}/${id}/add-review`, body);
  },
};
